import React, { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { useClickAway } from 'react-use';
import ReactQuill, { Quill } from 'react-quill';
import classnames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import 'react-quill/dist/quill.snow.css';

import CustomLink from './quill-addons/quill-link-format';
import TextEditorToolbarComponent from './react-quill-text-editor-toolbar/react-quill-text-editor-toolbar-component';

import './react-quill-text-editor-component.scss';

const Block = Quill.import('blots/block');
Block.tagName = 'DIV';

Quill.register(Block, true);
Quill.register(CustomLink);

const EDITOR_THEME = 'snow';
const DEBOUNCE_TIME = 250;

const disableToolbarTabIndex = () => {
  [...document.querySelectorAll('.ql-toolbar *')].forEach((i) => {
    i.setAttribute('tabindex', -1);
  });
};

function ReactQuillTextEditorComponent({
  placeholder = null,
  editorValue,
  onEditorValueChange = () => {},
  disabled = false,
  onEditorFocus = () => {},
  onEditorBlur = () => {},
  hideToolbar = false,
  className,
}) {
  /*
    State
  */
  const wrapperRef = useRef(null);
  const editorRef = useRef(null);
  const editorId = useRef(uuidv4().substr(0, 8));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (editorRef.current) {
      const editor = editorRef.current.getEditor();
      editor.root.dataset.placeholder = placeholder || '';

      disableToolbarTabIndex();
    }
  }, [placeholder]);

  useClickAway(wrapperRef, () => {
    if (isFocused) {
      setIsFocused(false);
      editorRef.current.blur();
      onEditorBlur();
    }
  });

  /*
    Handlers
  */

  const handleEditorFocus = useCallback(() => {
    setIsFocused(true);
    onEditorFocus();
  }, [onEditorFocus, setIsFocused]);

  const handleEditorValueChange = useCallback(
    (text) => {
      onEditorValueChange(text);
    },
    [onEditorValueChange]
  );

  return (
    <div
      ref={wrapperRef}
      className={classnames('react-quill-text-editor-component', className, {
        'tool-bar-hidden': hideToolbar,
        'is-focus': isFocused && !hideToolbar,
      })}
    >
      <ReactQuill
        ref={editorRef}
        onFocus={handleEditorFocus}
        theme={EDITOR_THEME}
        value={editorValue || ''}
        onChange={debounce(handleEditorValueChange, DEBOUNCE_TIME)}
        placeholder={placeholder}
        modules={{
          toolbar: {
            container: `#t-${editorId.current}`,
          },
        }}
        readOnly={disabled}
      />
      <TextEditorToolbarComponent hidden={hideToolbar} editorId={editorId.current} />
    </div>
  );
}

export default ReactQuillTextEditorComponent;
